import React from 'react';
import {
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Divider,
  Paper
} from '@mui/material';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import CodeIcon from '@mui/icons-material/Code';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ExploreIcon from '@mui/icons-material/Explore';
import SecurityIcon from '@mui/icons-material/Security';
import ExtensionIcon from '@mui/icons-material/Extension';
import StorageIcon from '@mui/icons-material/Storage';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoIcon from '@mui/icons-material/Info';
import BuildIcon from '@mui/icons-material/Build';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import ShieldIcon from '@mui/icons-material/Shield';
import BugReportIcon from '@mui/icons-material/BugReport';
import WarningIcon from '@mui/icons-material/Warning';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import TuneIcon from '@mui/icons-material/Tune';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import ScheduleIcon from '@mui/icons-material/Schedule';

// Simple feature badge component
const FeatureBadge: React.FC<{ label: string; variant?: 'primary' | 'warning' | 'secondary' }> = ({ 
  label, 
  variant = 'primary' 
}) => (
  <Chip
    label={label}
    size="small"
    color={variant}
    variant="outlined"
    sx={{
      fontSize: '0.65rem',
      height: 18,
      ml: 1,
      '& .MuiChip-label': { px: 0.8 }
    }}
  />
);

const ToolsTab: React.FC = () => {
  return (
    <Box>
      {/* Introduction Box */}
      <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2, backgroundColor: 'primary.lighter', borderLeft: '5px solid', borderColor: 'primary.main' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <BuildIcon sx={{ fontSize: '2.5rem', mr: 1.5, color: 'primary.main' }} />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.dark' }}>
            Tools & Utilities
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary" paragraph>
          AppConfig² provides a comprehensive collection of <strong>specialized tools</strong> designed to enhance your Microsoft Entra ID application management experience.
          These utilities help you test, analyze, debug, and optimize your application configurations with precision and efficiency.
        </Typography>
        <Typography variant="body2" color="text.primary">
          Each tool is purpose-built to address specific aspects of application lifecycle management, from authentication testing to comprehensive security analysis and schema extensions.
          Note that some tools have different access levels depending on your edition.
        </Typography>
      </Paper>

      <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mt: 3, mb: 2, borderBottom: '2px solid', borderColor: 'primary.light', pb: 1 }}>
        Available Tools
      </Typography>

      <Grid container spacing={3}>
        {/* NEW: Raw OAuth Tester */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LockOpenIcon color="primary" sx={{ mr: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <Typography variant="h6">Raw OAuth Tester</Typography>
                  <FeatureBadge label="New" />
                </Box>
              </Box>
              <Typography paragraph variant="body2" color="text.secondary">
                Low-level AAD v2.0 authorize URL builder for interactive testing of implicit sign-in and token parsing.
                Useful to quickly validate redirect URIs, state/nonce handling, and raw id/access token contents without SDK abstractions.
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="• Build and launch raw /authorize requests (implicit: token id_token)" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Choose tenant authority (common/organizations/current tenant)" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• See a parsed view of the last authorization request" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Decode returned tokens in-place; surface OAuth errors from callback" />
                </ListItem>
              </List>
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip size="small" label="Implicit only" sx={{ bgcolor: '#fff8e1', color: '#ff8f00' }} />
                <Chip size="small" label="No SDK" sx={{ bgcolor: '#e0f2f1', color: '#00695c' }} />
                <Chip size="small" label="Tenant switch" sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* NEW: OData Query Builder */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TuneIcon color="primary" sx={{ mr: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <Typography variant="h6">OData Query Builder</Typography>
                  <FeatureBadge label="New" />
                </Box>
              </Box>
              <Typography paragraph variant="body2" color="text.secondary">
                Compose Microsoft Graph OData queries visually. Focuses on crafting $select, $filter, $orderby, $top, and $count for common resources
                and executing them with paging support.
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="• Field suggestions for applications, servicePrincipals, users, and groups" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Visual filters with contains/startswith/endswith and comparison operators" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Fetch-all paging with @odata.nextLink, optional $count (ConsistencyLevel)" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Toggle v1.0/beta; copy URL; download JSON results" />
                </ListItem>
              </List>
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip size="small" label="$select/$filter" sx={{ bgcolor: '#e8eaf6', color: '#3949ab' }} />
                <Chip size="small" label="Paging" sx={{ bgcolor: '#f3e5f5', color: '#7b1fa2' }} />
                <Chip size="small" label="v1.0 / beta" sx={{ bgcolor: '#e0f2f1', color: '#00695c' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <VerifiedUserIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Auth Flow Tester</Typography>
              </Box>
              <Typography paragraph variant="body2" color="text.secondary">
                Test various authentication flows for your applications. This tool helps you:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="• Test OAuth 2.0 authorization code flow" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Test OAuth 2.0 implicit flow" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Test client credentials flow" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Test hybrid flow" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Validate OIDC configurations" />
                </ListItem>
              </List>
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip
                  size="small"
                  label="Authorization Code"
                  sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }}
                />
                <Chip
                  size="small"
                  label="Implicit Flow"
                  sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }}
                />
                <Chip
                  size="small"
                  label="Client Credentials"
                  sx={{ bgcolor: '#fff3e0', color: '#e65100' }}
                />
                <Chip
                  size="small"
                  label="Hybrid Flow"
                  sx={{ bgcolor: '#fce4ec', color: '#c2185b' }}
                />
                <Chip
                  size="small"
                  label="OIDC"
                  sx={{ bgcolor: '#f3e5f5', color: '#7b1fa2' }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* NEW: Token Scope Requester */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 2, display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CenterFocusStrongIcon color="primary" sx={{ mr: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <Typography variant="h6">Token Scope Requester</Typography>
                  <FeatureBadge label="New" />
                </Box>
              </Box>
              <Typography paragraph variant="body2" color="text.secondary">
                Request delegated tokens for Graph or custom APIs with explicit scopes or resource/.default.
                Diagnose consent issues, missing scopes, and app roles; test endpoints with the acquired token.
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="• Tenant authority dropdown; switch user via popup; optional clientId override" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Requested vs granted delta; consent status helper; app-role assignments" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• One-click Graph test presets (GET) with hint scopes" />
                </ListItem>
              </List>
              <Box sx={{ mt: 'auto', pt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip size="small" label="Scopes delta" sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }} />
                <Chip size="small" label="Consent insights" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                <Chip size="small" label="App roles" sx={{ bgcolor: '#fff3e0', color: '#e65100' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* NEW: Secrets Expiry Monitor */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 2, display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ScheduleIcon color="primary" sx={{ mr: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <Typography variant="h6">Secrets Expiry Monitor</Typography>
                  <FeatureBadge label="New" />
                </Box>
              </Box>
              <Typography paragraph variant="body2" color="text.secondary">
                Inventory application and service principal credentials and track upcoming expirations. Filter, group by app, and export for audit.
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="• Scan password and key credentials; include Service Principals optionally" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Risk buckets (<=90/30/7 days) and expired status; search by app or displayName" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• CSV export of filtered results" />
                </ListItem>
              </List>
              <Box sx={{ mt: 'auto', pt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip size="small" label="Expiry tracking" sx={{ bgcolor: '#fff8e1', color: '#ff8f00' }} />
                <Chip size="small" label="CSV export" sx={{ bgcolor: '#f3e5f5', color: '#7b1fa2' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CodeIcon color="primary" sx={{ mr: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <Typography variant="h6">Manifest Editor</Typography>
                  <FeatureBadge label="AppConfig² Only" />
                </Box>
              </Box>
              <Typography paragraph variant="body2" color="text.secondary">
                Edit application manifests directly to fine-tune application configurations (AppConfig² only). 
                In AppTesting, manifests are available in read-only viewer mode. This tool helps you:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="• View and edit the full JSON manifest" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Make advanced configuration changes" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Apply manifest changes directly to applications" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Validate manifest syntax before applying" />
                </ListItem>
              </List>
              <Box sx={{ mt: 2, bgcolor: '#f0f7ff', p: 2, borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Note:</strong> Direct manifest editing gives you full control but requires careful attention.
                  Use the structured interface for common tasks when possible.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ContentCopyIcon color="primary" sx={{ mr: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <Typography variant="h6">Claims Mapping Policy Tool</Typography>
                  <FeatureBadge label="AppConfig² Only" />
                </Box>
              </Box>
              <Typography paragraph variant="body2" color="text.secondary">
                Create and manage claims mapping policies for your applications. This tool helps you:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="• Create new policies with custom claims" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Assign policies to specific applications" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Review and edit existing policies" />
                </ListItem>
              </List>
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip size="small" label="Custom claims" sx={{ bgcolor: '#f3e5f5', color: '#7b1fa2' }} />
                <Chip size="small" label="Assign to apps" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                <Chip size="small" label="Review policies" sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <VpnKeyIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">JWT Token Decoder</Typography>
              </Box>
              <Typography paragraph variant="body2" color="text.secondary">
                Decode and analyze JWT tokens to understand their contents. This tool helps you:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="• View token headers, payloads, and signatures" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Analyze claims and their values" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Verify token validity and expiration" />
                </ListItem>
              </List>
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip size="small" label="Headers" sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }} />
                <Chip size="small" label="Claims" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                <Chip size="small" label="Expiry (exp/nbf)" sx={{ bgcolor: '#fff3e0', color: '#e65100' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 2, display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ExploreIcon color="primary" sx={{ mr: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <Typography variant="h6">Graph Explorer</Typography>
                  <FeatureBadge label="Read-only in AppTesting" variant="warning" />
                </Box>
              </Box>
              <Typography paragraph variant="body2" color="text.secondary">
                Test and explore Microsoft Graph API endpoints. In AppTesting, this provides read-only access (Graph Viewer mode). 
                This tool helps you:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="• Make Graph API requests directly from the UI" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• View response data in a formatted display" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Test different Graph API operations (GET only in AppTesting)" />
                </ListItem>
              </List>
              <Box sx={{ mt: 'auto', pt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip size="small" label="GET (AppTesting)" sx={{ bgcolor: '#fff8e1', color: '#ff8f00' }} />
                <Chip size="small" label="Response viewer" sx={{ bgcolor: '#e0f2f1', color: '#00796b' }} />
                <Chip size="small" label="Headers/Body" sx={{ bgcolor: '#e8eaf6', color: '#3949ab' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* UPDATED: Enhanced Security Analysis Tool */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 2, border: '2px solid', borderColor: 'secondary.main' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon color="secondary" sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ color: 'secondary.main' }}>Security Analyzer</Typography>
                <Chip 
                  label="Enhanced" 
                  size="small" 
                  color="secondary" 
                  sx={{ ml: 1, fontSize: '0.7rem' }}
                />
              </Box>
              <Typography paragraph variant="body2" color="text.secondary">
                Comprehensive security analysis for your applications including permissions, configurations, and security posture assessment. This enhanced tool helps you:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="• Analyze all application permissions and identify risks" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Perform security configuration checks and compliance validation" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Calculate security scores and risk assessments" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Generate actionable security recommendations" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Export detailed security reports for auditing" />
                </ListItem>
              </List>
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip
                  size="small"
                  label="Permission Analysis"
                  sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }}
                />
                <Chip
                  size="small"
                  label="Security Checks"
                  sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }}
                />
                <Chip
                  size="small"
                  label="Risk Assessment"
                  sx={{ bgcolor: '#fce4ec', color: '#c2185b' }}
                />
                <Chip
                  size="small"
                  label="Compliance"
                  sx={{ bgcolor: '#f3e5f5', color: '#7b1fa2' }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* NEW: Attack Surface Analyzer */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 2, border: '2px solid', borderColor: 'warning.main' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <GpsFixedIcon sx={{ color: 'warning.main', mr: 1 }} />
                <Typography variant="h6" sx={{ color: 'warning.main' }}>Attack Surface Analyzer</Typography>
                <Chip 
                  label="New" 
                  size="small" 
                  color="warning" 
                  sx={{ ml: 1, fontSize: '0.7rem' }}
                />
              </Box>
              <Typography paragraph variant="body2" color="text.secondary">
                Advanced attack surface analysis from an adversary's perspective. Identify potential attack vectors, vulnerabilities, and exposure points in your Azure AD applications. This tool helps you:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="• Map authentication flow attack vectors and entry points" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Detect redirect URI vulnerabilities and open redirect risks" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Analyze token handling and session management flaws" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Identify API exposure and privilege escalation paths" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Assess cross-tenant access and tenant isolation risks" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Generate attack path visualizations and impact assessments" />
                </ListItem>
              </List>
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip
                  size="small"
                  label="Attack Vectors"
                  sx={{ bgcolor: '#ffebee', color: '#c62828' }}
                />
                <Chip
                  size="small"
                  label="Vulnerability Scan"
                  sx={{ bgcolor: '#fff3e0', color: '#e65100' }}
                />
                <Chip
                  size="small"
                  label="Exposure Analysis"
                  sx={{ bgcolor: '#fff8e1', color: '#ff8f00' }}
                />
                <Chip
                  size="small"
                  label="Risk Mapping"
                  sx={{ bgcolor: '#fce4ec', color: '#c2185b' }}
                />
              </Box>
              <Box sx={{ mt: 2, bgcolor: '#fff3e0', p: 2, borderRadius: 1, border: '1px solid #ffb74d' }}>
                <Typography variant="caption" sx={{ color: '#e65100', fontWeight: 'bold' }}>
                  <WarningIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                  Advanced Security Tool
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  This tool performs adversarial analysis to identify potential attack surfaces. 
                  Results should be used for security hardening and risk mitigation.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 2, display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ExtensionIcon color="primary" sx={{ mr: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <Typography variant="h6">Schema Extensions Manager</Typography>
                  <FeatureBadge label="AppConfig² Only" />
                </Box>
              </Box>
              <Typography paragraph variant="body2" color="text.secondary">
                Create and manage directory extensions for Entra ID objects. This tool helps you:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="• Create custom directory extensions for users, groups and applications" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Discover existing directory extensions in your tenant" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Explore Entra Connect synchronized extensions" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• View and query extension attributes applied to objects" />
                </ListItem>
              </List>
              <Box sx={{ mt: 'auto', pt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip
                  size="small"
                  label="Directory Extensions"
                  sx={{ bgcolor: '#e8eaf6', color: '#3949ab' }}
                />
                <Chip
                  size="small"
                  label="Extension Attributes"
                  sx={{ bgcolor: '#e0f2f1', color: '#00796b' }}
                />
                <Chip
                  size="small"
                  label="Entra Connect"
                  sx={{ bgcolor: '#fff8e1', color: '#ff8f00' }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* COMPARISON: When to use which tool */}
      <Card sx={{ mt: 4, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Choosing the right tool for the job
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Graph Explorer vs. OData Query Builder
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><ExploreIcon color="primary" /></ListItemIcon>
                  <ListItemText
                    primary="Graph Explorer"
                    secondary="Full request runner for Microsoft Graph. Paste any endpoint, set method/body/headers, and execute. Great for trying APIs end-to-end and consenting missing scopes on the fly."
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><TuneIcon color="primary" /></ListItemIcon>
                  <ListItemText
                    primary="OData Query Builder"
                    secondary="Purpose-built for crafting OData parameters ($select, $filter, $orderby, $top, $count) over common Graph collections, with paging and download. Ideal when you need precise query shapes and large result navigation."
                  />
                </ListItem>
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Auth Flow Tester vs. Raw OAuth Tester
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><VerifiedUserIcon color="primary" /></ListItemIcon>
                  <ListItemText
                    primary="Auth Flow Tester"
                    secondary="Guided experience powered by libraries for common OAuth/OIDC flows (authorization code, client credentials, etc.). Best for validating app setup with SDK-friendly defaults."
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><LockOpenIcon color="primary" /></ListItemIcon>
                  <ListItemText
                    primary="Raw OAuth Tester"
                    secondary="Bare-metal authorize URL builder focused on the implicit flow and token parsing. Best for debugging redirect URIs, parameters, and raw token payloads without SDK behavior."
                  />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* NEW: Security Tools Deep Dive Section */}
      <Card sx={{ mt: 4, borderRadius: 2, bgcolor: '#f8f9fa' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <ShieldIcon sx={{ mr: 1, color: 'secondary.main' }} />
            Security Analysis Tools
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <SecurityIcon sx={{ mr: 1, color: 'secondary.main' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    Security Analyzer
                  </Typography>
                </Box>
                <Typography variant="body2" paragraph>
                  The enhanced Security Analyzer provides comprehensive security posture assessment for your Azure AD applications, 
                  combining permission analysis, configuration checks, and security best practices validation.
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="secondary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Permission Risk Assessment" 
                      secondary="Analyze all granted permissions and identify high-risk or excessive privileges" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="secondary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Security Configuration Checks" 
                      secondary="Validate application configurations against security best practices" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="secondary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Compliance Reporting" 
                      secondary="Generate detailed security reports for auditing and compliance purposes" 
                    />
                  </ListItem>
                </List>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <GpsFixedIcon sx={{ mr: 1, color: 'warning.main' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    Attack Surface Analyzer
                  </Typography>
                </Box>
                <Typography variant="body2" paragraph>
                  The Attack Surface Analyzer takes a red-team approach to identify potential attack vectors and vulnerabilities 
                  in your Azure AD applications from an adversary's perspective.
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon sx={{ color: 'warning.main' }} fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Attack Vector Identification" 
                      secondary="Discover authentication flow vulnerabilities and exploitation paths" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon sx={{ color: 'warning.main' }} fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Exposure Point Mapping" 
                      secondary="Map API endpoints, redirect URIs, and other potential attack surfaces" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon sx={{ color: 'warning.main' }} fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Risk Prioritization" 
                      secondary="Calculate exposure scores and risk ratings for identified vulnerabilities" 
                    />
                  </ListItem>
                </List>
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ p: 2, backgroundColor: '#e3f2fd', borderRadius: 2, border: '1px solid #90caf9' }}>
                <Typography variant="subtitle2" gutterBottom sx={{ color: '#1565c0' }}>
                  <BugReportIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                  Security Analysis Best Practices
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <InfoIcon fontSize="small" color="info" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Regular Security Reviews"
                          secondary="Perform security analysis after any application configuration changes"
                        />
                      </ListItem>
                    </List>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <InfoIcon fontSize="small" color="info" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Follow Remediation Guidance"
                          secondary="Implement recommended security mitigations promptly"
                        />
                      </ListItem>
                    </List>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <InfoIcon fontSize="small" color="info" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Monitor Security Scores"
                          secondary="Track security improvements over time and maintain high scores"
                        />
                      </ListItem>
                    </List>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Schema Extensions Details Section - KEPT EXISTING */}
      <Card sx={{ mt: 4, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 0, flex: 1 }}>Schema Extensions Manager</Typography>
            <FeatureBadge label="AppConfig² Only" />
          </Box>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ExtensionIcon sx={{ mr: 1, color: '#3f51b5' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    Directory Extensions
                  </Typography>
                </Box>
                <Typography variant="body2" paragraph>
                  Directory extensions allow you to add custom properties to Entra ID objects like users and groups. 
                  These extensions are created in an application and can be used across your tenant.
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Application-owned extensions" 
                      secondary="Each extension is owned by an application in your tenant" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Multiple data types supported" 
                      secondary="String, Integer, Boolean, DateTime and Binary values" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Target multiple object types" 
                      secondary="Apply to users, groups, devices, applications, or service principals" 
                    />
                  </ListItem>
                </List>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <StorageIcon sx={{ mr: 1, color: '#00796b' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    Extension Attributes
                  </Typography>
                </Box>
                <Typography variant="body2" paragraph>
                  Extension attributes let you view and query the values of directory extensions that have been 
                  applied to objects in your directory, helping you understand how extensions are used.
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Query attribute values" 
                      secondary="Find objects with specific extension attribute values" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Data visualization" 
                      secondary="See how extensions are used across your tenant" 
                    />
                  </ListItem>
                </List>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <SyncAltIcon sx={{ mr: 1, color: '#ff8f00' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    Entra Connect Extensions
                  </Typography>
                </Box>
                <Typography variant="body2" paragraph>
                  Entra Connect extensions are special directory extensions synchronized from on-premises 
                  Active Directory attributes through Entra Connect.
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Discover synchronized attributes" 
                      secondary="Identify what on-premises attributes are available in Entra ID" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Explore extension_* format values" 
                      secondary="See the special format used by Entra Connect extensions" 
                    />
                  </ListItem>
                </List>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Best Practices for Directory Extensions
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <InfoIcon fontSize="small" color="info" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Use a dedicated application for extensions"
                      secondary="Create a specific application to own your directory extensions"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <InfoIcon fontSize="small" color="info" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Document your schema"
                      secondary="Keep track of what extensions you create and their purpose"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <InfoIcon fontSize="small" color="info" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Use descriptive naming"
                      secondary="Choose clear names that indicate the extension's purpose"
                    />
                  </ListItem>
                </List>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ToolsTab;